(() => {
  const source = document.getElementById('cz-pdp-shared-config');
  if (!source || window.czPdpShared) return;
  let config;
  try { config = JSON.parse(source.textContent); } catch (_) { return; }
  if (!config || !config.productId || !config.kind) return;
  const productForm = document.querySelector(`.product-form[data-product-id="${config.productId}"]`);
  if (!productForm) {
    if (source.dataset.czPdpSharedWaiting) return;
    source.dataset.czPdpSharedWaiting = 'true';
    const scriptUrl = document.currentScript?.src;
    const retry = new MutationObserver(() => {
      if (!document.querySelector(`.product-form[data-product-id="${config.productId}"]`)) return;
      retry.disconnect();
      delete source.dataset.czPdpSharedWaiting;
      if (!scriptUrl || window.czPdpShared) return;
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.addEventListener('load', () => script.remove(), {once: true});
      document.head.append(script);
    });
    const observe = () => retry.observe(document.body, {childList: true, subtree: true});
    if (document.body) observe();
    else document.addEventListener('DOMContentLoaded', observe, {once: true});
    setTimeout(() => retry.disconnect(), 30000);
    return;
  }
  source.dataset.czPdpMaster = config.master || config.kind;
  source.dataset.czPdpProfile = config.profile || config.kind;
  document.documentElement.dataset.czPdpMaster = config.master || config.kind;
  document.documentElement.dataset.czPdpProfile = config.profile || config.kind;
  productForm.dataset.czPdpMaster = config.master || config.kind;
  productForm.dataset.czPdpProfile = config.profile || config.kind;

  const DRAPERY_COMPONENT_VERSION = 'drapery-guided-v4';
  const textValue = (value, fallback, max = 1200) => {
    const text = typeof value === 'string' ? value.trim() : '';
    return (text || fallback).slice(0, max);
  };
  const safeUrl = (value, fallback) => {
    const candidate = textValue(value, fallback, 1200);
    try {
      const url = new URL(candidate, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? candidate : fallback;
    } catch (_) { return fallback; }
  };
  const productTemplate = (value, fallback) => {
    const template = textValue(value, fallback, 180);
    return template.includes('{product}') ? template : fallback;
  };
  let OPERABLE_PRESENTATION = {};
  try { OPERABLE_PRESENTATION = JSON.parse(document.getElementById('cz-drapery-operable-presentation')?.textContent || '{}'); }
  catch (_) { OPERABLE_PRESENTATION = {}; }
  const chapterDefaults = Object.freeze({
    meet: Object.freeze({kind: 'meet', titleSource: 'meetName', titlePrefix: 'Meet'}),
    tools: Object.freeze({kind: 'tools', title: 'Helpful tools'}),
    homes: Object.freeze({kind: 'homes', title: 'Real Home Inspiration', nestedTitle: true}),
    hardware: Object.freeze({kind: 'hardware', title: 'Hardware for your header'}),
    reviews: Object.freeze({kind: 'reviews', title: 'Reviews'}),
    support: Object.freeze({kind: 'support', title: 'FAQs'})
  });
  const defaultChapterOrder = Object.keys(chapterDefaults);
  const requestedChapterOrder = Array.isArray(OPERABLE_PRESENTATION.chapterOrder) ? OPERABLE_PRESENTATION.chapterOrder : [];
  const chapterOrder = requestedChapterOrder.length === defaultChapterOrder.length
    && new Set(requestedChapterOrder).size === defaultChapterOrder.length
    && requestedChapterOrder.every(kind => defaultChapterOrder.includes(kind))
    ? requestedChapterOrder : defaultChapterOrder;
  const chapterCopy = OPERABLE_PRESENTATION.chapters || {};
  const chapterDefinition = kind => {
    const fallback = chapterDefaults[kind];
    if (kind === 'meet') return Object.freeze({...fallback, titlePrefix: textValue(chapterCopy.meetPrefix, fallback.titlePrefix, 80)});
    return Object.freeze({...fallback, title: textValue(chapterCopy[kind], fallback.title, 120)});
  };
  const DRAPERY_PAGE_STRUCTURE = Object.freeze({
    version: 'drapery-page-v2',
    chapters: Object.freeze(chapterOrder.map(chapterDefinition)),
    navigation: Object.freeze([
      Object.freeze({key: 'customize', target: 'customize'}),
      Object.freeze({key: 'details', target: 'details'}),
      Object.freeze({key: 'gallery', target: 'homes'}),
      Object.freeze({key: 'reviews', target: 'reviews'})
    ])
  });
  const DRAPERY_POLICY = Object.freeze({
    behavior: Object.freeze({openNextStep: true}),
    style: Object.freeze({stepBorderWidth: '1px', stepBorderColor: '#ddd8d3'})
  });
  const groupCopy = OPERABLE_PRESENTATION.groups || {};
  const helpCopy = (key, defaults) => {
    const source = groupCopy[key]?.help || {};
    const result = {...defaults};
    ['label', 'title', 'text', 'linkText', 'toolText', 'drawerTitle', 'drawerIntro', 'drawerLinkText'].forEach(field => {
      if (field in defaults) result[field] = textValue(source[field], defaults[field], field.includes('text') || field.includes('Intro') ? 1200 : 180);
    });
    ['href', 'drawerHref'].forEach(field => {
      if (field in defaults) result[field] = safeUrl(source[field], defaults[field]);
    });
    return Object.freeze(result);
  };
  const DRAPERY_GROUP_PRESENTATION = Object.freeze({
    color: Object.freeze({title: textValue(groupCopy.color?.title, 'Color', 80)}),
    header: Object.freeze({
      title: textValue(groupCopy.header?.title, 'Header', 80),
      help: helpCopy('header', {kind: 'header', label: 'About Header Style', title: 'Header Style', text: 'Compare header styles and choose the construction that works with your hardware.', href: '/pages/header', linkText: 'View the Header Guide', drawerTitle: 'Header Guidelines', drawerIntro: 'Explore each header style to find the look and hardware that suit your curtains.', drawerHref: '/pages/header', drawerLinkText: 'View full header guide'})
    }),
    size: Object.freeze({
      title: textValue(groupCopy.size?.title, 'Size', 80),
      help: helpCopy('size', {kind: 'size', label: 'About Size', title: 'Size', text: 'Choose the guide that matches your Header Style and Header Type. Width and Length are the finished measurements for one panel.', href: '/pages/drapery-measurement', linkText: 'Use Measurement Tool', toolText: 'Open Measurement Tool'})
    }),
    lining: Object.freeze({
      title: textValue(groupCopy.lining?.title, 'Lining', 80),
      help: helpCopy('lining', {kind: 'liner', label: 'About Lining', title: 'Lining', text: 'Compare lining choices for light control, privacy, insulation, and drape.', href: '/pages/liner', linkText: 'View the Liner Guide', drawerTitle: 'Lining Guidelines', drawerIntro: 'A liner is an extra fabric layer sewn to the back of your curtain. It adds body and helps with light control, privacy and insulation.', drawerHref: '/pages/liner', drawerLinkText: 'View full liner guide'})
    }),
    accessories: Object.freeze({
      title: textValue(groupCopy.accessories?.title, 'Accessories & extras', 80),
      help: helpCopy('accessories', {kind: 'accessories', label: 'About Accessories & extras', title: 'Accessories & extras', text: 'Choose the optional finishing details that fit your room and installation.'})
    }),
    'size-protection': Object.freeze({
      title: textValue(groupCopy['size-protection']?.title, 'Size Protection', 80),
      help: helpCopy('size-protection', {kind: 'size-protection', label: 'About Size Protection', title: 'Size Protection', text: 'Review the available size protection option before completing the configuration.'})
    }),
    'included-benefits': Object.freeze({
      // Capability preset: applied only when a verified product profile explicitly declares this key.
      title: textValue(groupCopy['included-benefits']?.title, 'Included benefits', 80),
      emptySummary: 'Included',
      help: helpCopy('included-benefits', {kind: 'included-benefits', label: 'About Included benefits', title: 'Included benefits', text: 'Lifetime Limited Warranty and adjustable hooks are included at no added charge; this custom curtain is sold per panel.'})
    })
  });
  const sampleCopy = OPERABLE_PRESENTATION.samples || {};
  const DRAPERY_SAMPLE_PRESENTATION = Object.freeze({
    prefix: '',
    dialogId: 'cz-drapery-samples',
    dialogTitleId: 'cz-drapery-samples-title',
    swatches: Object.freeze({
      order: Object.freeze({defaultLabel: textValue(sampleCopy.orderDefault, 'Order Free Swatches', 180), productLabel: productTemplate(sampleCopy.orderProduct, 'Order {product} Swatches')}),
      view: Object.freeze({defaultLabel: textValue(sampleCopy.viewDefault, 'View Swatches', 180), productLabel: productTemplate(sampleCopy.viewProduct, 'View {product} Swatches')}),
      empty: Object.freeze({defaultLabel: textValue(sampleCopy.emptyDefault, 'View Swatch Options', 180), productLabel: productTemplate(sampleCopy.emptyProduct, 'View {product} Swatch Options')})
    }),
    booklet: Object.freeze({
      addLabel: productTemplate(sampleCopy.bookletAdd, 'Buy {product} Booklet'),
      removeLabel: productTemplate(sampleCopy.bookletRemove, 'Remove {product} Booklet'),
      unavailableLabel: productTemplate(sampleCopy.bookletUnavailable, '{product} Booklet Unavailable')
    })
  });
  const sampleLabel = (template, subject = '') => DRAPERY_SAMPLE_PRESENTATION.prefix + template.replace('{product}', subject.trim());
  const composeDraperySampleActions = actions => {
    if (!actions || typeof actions !== 'object') return undefined;
    const composed = {};
    const swatches = actions.swatches;
    const swatchPresentation = swatches && DRAPERY_SAMPLE_PRESENTATION.swatches[swatches.intent];
    if (swatchPresentation) {
      const subject = typeof swatches.subject === 'string' ? swatches.subject.trim() : '';
      composed.swatches = {
        intent: swatches.intent,
        subject,
        mode: 'dialog',
        dialogId: DRAPERY_SAMPLE_PRESENTATION.dialogId,
        dialogTitleId: DRAPERY_SAMPLE_PRESENTATION.dialogTitleId,
        label: DRAPERY_SAMPLE_PRESENTATION.prefix + (subject ? swatchPresentation.productLabel.replace('{product}', subject) : swatchPresentation.defaultLabel)
      };
    }
    const bookletSubject = typeof actions.booklet?.subject === 'string' ? actions.booklet.subject.trim() : '';
    if (bookletSubject) {
      composed.booklet = {
        subject: bookletSubject,
        mode: 'cart-toggle',
        label: sampleLabel(DRAPERY_SAMPLE_PRESENTATION.booklet.addLabel, bookletSubject),
        removeLabel: sampleLabel(DRAPERY_SAMPLE_PRESENTATION.booklet.removeLabel, bookletSubject),
        unavailableLabel: sampleLabel(DRAPERY_SAMPLE_PRESENTATION.booklet.unavailableLabel, bookletSubject)
      };
    }
    return Object.keys(composed).length ? composed : undefined;
  };
  const syncDraperySampleActions = (root, actions, enabled) => {
    if (!root || !enabled || !actions?.swatches) return null;
    let sampleLinks = root.querySelector('.cz-fabric-sample-links')
      || (root.previousElementSibling?.matches('.cz-fabric-sample-links') ? root.previousElementSibling : null);
    if (!sampleLinks) {
      sampleLinks = document.createElement('div');
      sampleLinks.className = 'cz-fabric-sample-links';
    }
    const syncAction = (action, kind) => {
      const attribute = kind === 'swatches' ? 'czOrderSwatches' : 'czBuyBooklet';
      let control = sampleLinks.querySelector(`[data-${kind === 'swatches' ? 'cz-order-swatches' : 'cz-buy-booklet'}]`);
      if (!action) {
        control?.remove();
        return;
      }
      if (!control) {
        control = document.createElement(action.mode === 'link' ? 'a' : 'button');
        sampleLinks.append(control);
      }
      if (control.tagName === 'BUTTON') {
        control.type = 'button';
        control.dataset[attribute] = '';
        if (action.mode === 'dialog') {
          control.setAttribute('aria-haspopup', 'dialog');
          control.setAttribute('aria-controls', action.dialogId);
        } else {
          control.removeAttribute('aria-haspopup');
          control.removeAttribute('aria-controls');
        }
      } else {
        control.href = action.href;
      }
      control.dataset.czSampleAction = kind;
      control.dataset.czSampleLabel = action.label;
      if (action.removeLabel) control.dataset.czSampleRemoveLabel = action.removeLabel;
      else delete control.dataset.czSampleRemoveLabel;
      if (action.unavailableLabel) control.dataset.czSampleUnavailableLabel = action.unavailableLabel;
      else delete control.dataset.czSampleUnavailableLabel;
      let label = action.label;
      if (kind === 'booklet') {
        if (['Adding…', 'Removing…'].includes(control.textContent)) return;
        const priceSuffix = control.dataset.czSamplePriceSuffix || '';
        if (action.available === false && control.dataset.czBookletAction !== 'remove') control.disabled = true;
        label = control.dataset.czBookletAction === 'remove'
          ? action.removeLabel
          : action.available === false ? action.unavailableLabel : action.label + priceSuffix;
      }
      if (control.textContent !== label) control.textContent = label;
    };
    const dialog = document.getElementById(actions.swatches.dialogId);
    const hasProductSamples = Boolean(dialog?.querySelector('[data-cz-add-sample]'));
    const emptyPresentation = DRAPERY_SAMPLE_PRESENTATION.swatches.empty;
    const swatches = !hasProductSamples && actions.swatches.intent === 'order'
      ? {...actions.swatches, label: DRAPERY_SAMPLE_PRESENTATION.prefix + (actions.swatches.subject ? emptyPresentation.productLabel.replace('{product}', actions.swatches.subject) : emptyPresentation.defaultLabel)}
      : actions.swatches;
    let bookletData;
    try { bookletData = JSON.parse(document.getElementById('cz-drapery-booklet-data')?.textContent || '{}'); }
    catch (_) { bookletData = {}; }
    const existingBooklet = sampleLinks.querySelector('[data-cz-buy-booklet]');
    const booklet = actions.booklet && (bookletData.id || existingBooklet?.dataset.czBookletAction === 'remove')
      ? {...actions.booklet, available: bookletData.available === true}
      : undefined;
    syncAction(swatches, 'swatches');
    syncAction(booklet, 'booklet');
    const dialogTitle = document.getElementById(swatches.dialogTitleId);
    const dialogLabel = dialog?.dataset.czSampleDialogLabel || swatches.label;
    if (dialogTitle && dialogTitle.textContent !== dialogLabel) dialogTitle.textContent = dialogLabel;
    let status = sampleLinks.querySelector('[data-cz-sample-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.czSampleStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      sampleLinks.append(status);
    }
    return sampleLinks;
  };
  const DRAPERY_GROUP_ORDER = Object.freeze(['color', 'header', 'size', 'lining', 'accessories']);
  const composeDraperyGroups = groups => (groups || []).map(group => {
    const shared = DRAPERY_GROUP_PRESENTATION[group.key];
    if (!shared || group.preservePresentation === true) return {...group, ids: [...(group.ids || [])], help: group.help ? {...group.help} : group.help};
    const {title: _legacyTitle, help: _legacyHelp, ...productFacts} = group;
    return {
      ...productFacts,
      ...shared,
      ids: [...(group.ids || [])],
      help: shared.help ? {...shared.help} : undefined
    };
  });
  const collectionDisplayName = value => {
    const fullName = (value || '').trim().replace(/\s+(?:Linen|Cotton|Wool|Velvet)$/i, '');
    return config.collection?.displayMode === 'before-pipe' ? fullName.split('|')[0].trim() : fullName;
  };
  const syncCollectionDisplayNames = collection => {
    if (!collection) return;
    const title = collection.querySelector('.ymq-option-title .ymq_lable');
    if (title && config.collection?.title) {
      if (title.textContent !== config.collection.title) title.textContent = config.collection.title;
      if (title.classList.contains('ymq-option-required')) title.classList.remove('ymq-option-required');
    }
    collection.querySelectorAll('.ymq-option-label').forEach(label => {
      const input = label.querySelector('input[type="radio"]');
      const visibleName = label.querySelector('.ymq-option-value');
      if (!input || !visibleName) return;
      const fullName = input.value.trim();
      const shortName = collectionDisplayName(fullName);
      if (visibleName.textContent !== shortName) visibleName.textContent = shortName;
      label.title = fullName;
    });
    const selected = collection.querySelector('input[type="radio"]:checked:not(:disabled)');
    const selectedName = collection.querySelector('.ymq-option-title-right-name');
    const selectedDisplay = collection.querySelector('.ymq-option-title-right');
    const shortSelectedName = collectionDisplayName(selected?.value);
    if (selectedName && selectedName.textContent !== shortSelectedName) selectedName.textContent = shortSelectedName;
    if (selectedDisplay) selectedDisplay.dataset.czCollectionDisplay = shortSelectedName;
    if (!collection._czCollectionNamesObserver) {
      collection._czCollectionNamesObserver = new MutationObserver(() => requestAnimationFrame(() => syncCollectionDisplayNames(collection)));
      collection._czCollectionNamesObserver.observe(collection, {childList: true, subtree: true, characterData: true});
    }
  };
  const groupDefinition = (groups, key) => groups?.find(group => group.key === key);
  const groupIndex = (groups, key) => groups?.findIndex(group => group.key === key) ?? -1;
  const nextGroupIndex = (groups, key) => {
    const index = groupIndex(groups, key);
    return index >= 0 && index < groups.length - 1 ? index + 1 : -1;
  };
  const continueLabel = (groups, key) => {
    const definition = groupDefinition(groups, key);
    return definition ? `Continue to ${definition.title}` : '';
  };
  const bindOptionMedia = (root, media) => {
    if (!root || !media || typeof media !== 'object') return 0;
    let bound = 0;
    root.querySelectorAll('.ymq-option-label').forEach(label => {
      const asset = media[label.querySelector('input[type="radio"]')?.value];
      const image = label.querySelector('.ymq-option-swatches-image');
      if (!asset?.url || !image) return;
      if (image.getAttribute('src') !== asset.url) image.src = asset.url;
      if (asset.alt && image.alt !== asset.alt) image.alt = asset.alt;
      bound += 1;
    });
    return bound;
  };
  if ((config.master || config.kind) === 'drapery') {
    config = {...config, groups: composeDraperyGroups(config.groups), sampleActions: composeDraperySampleActions(config.sampleActions)};
    const keys = config.groups.map(group => group.key);
    const uniqueKeys = new Set(keys).size === keys.length;
    if (!uniqueKeys) return;
    if (config.features?.guidedAdapter !== 'legacy-shared') {
      const stablePrefix = DRAPERY_GROUP_ORDER.every((key, index) => keys[index] === key);
      const finalKey = keys[DRAPERY_GROUP_ORDER.length];
      const supportedLength = keys.length === DRAPERY_GROUP_ORDER.length
        || (keys.length === DRAPERY_GROUP_ORDER.length + 1 && ['size-protection', 'included-benefits'].includes(finalKey));
      if (!stablePrefix || !supportedLength) return;
    }
  }
  if ((config.master || config.kind) === 'drapery') {
    productForm.style.setProperty('--cz-drapery-step-border-width', DRAPERY_POLICY.style.stepBorderWidth);
    productForm.style.setProperty('--cz-drapery-step-border-color', DRAPERY_POLICY.style.stepBorderColor);
  }

  const engine = {
    config,
    master: config.master || config.kind,
    draperyPolicy: DRAPERY_POLICY,
    presentation: Object.freeze({
      detailsEntryLabel: textValue(OPERABLE_PRESENTATION.detailsEntryLabel, 'Product details & guides', 180),
      realHomesTitle: textValue(chapterCopy.homes, 'Real Home Inspiration', 180)
    }),
    draperyGroupPresentation: DRAPERY_GROUP_PRESENTATION,
    draperySamplePresentation: DRAPERY_SAMPLE_PRESENTATION,
    composeDraperyGroups,
    composeDraperySampleActions,
    syncSampleActions(root) {
      return syncDraperySampleActions(root, config.sampleActions, config.features?.sampleActions);
    },
    bindOptionMedia,
    groupDefinition(key) {
      return groupDefinition(config.groups, key);
    },
    groupIndex(key) {
      return groupIndex(config.groups, key);
    },
    nextGroupIndex(key) {
      return nextGroupIndex(config.groups, key);
    },
    continueLabel(key) {
      return continueLabel(config.groups, key);
    },
    pageStructure: (config.master || config.kind) === 'drapery' ? DRAPERY_PAGE_STRUCTURE : null,
    chapterTitle(kind) {
      const definition = engine.pageStructure?.chapters.find(chapter => chapter.kind === kind);
      if (!definition) return '';
      if (!definition.titleSource) return definition.title;
      const prefix = definition.titlePrefix || '';
      const subject = config[definition.titleSource] || '';
      return [prefix, subject].filter(Boolean).join(' ');
    },
    chapterDefinitions() {
      if (engine.pageStructure) {
        return engine.pageStructure.chapters.map(definition => ({
          kind: definition.kind,
          title: definition.nestedTitle ? null : engine.chapterTitle(definition.kind)
        }));
      }
      const definitions = Array.isArray(config.chapters) ? config.chapters : [];
      const seen = new Set();
      return definitions.filter(definition => {
        if (!definition || typeof definition.kind !== 'string' || seen.has(definition.kind)) return false;
        seen.add(definition.kind);
        return true;
      });
    },
    chapter(kind, title, product = config.kind) {
      const namespace = engine.master === 'drapery' ? 'tarryn' : product;
      const node = document.createElement('section');
      node.id = `cz-${namespace}-chapter-${kind}`;
      node.className = `cz-pdp-chapter cz-${namespace}-chapter cz-${namespace}-chapter--${kind}`;
      if (engine.master === 'drapery') node.dataset.czComponentSource = 'tarryn-whole-page-master-v1';
      node.dataset.czChapter = kind;
      if (title) {
        const heading = document.createElement('h2');
        if (engine.master === 'drapery') heading.className = 'cz-chapter-heading';
        heading.textContent = title;
        node.append(heading);
      }
      return node;
    },
    mountChapters(main, definitions = engine.chapterDefinitions(), product = config.kind) {
      if (!main || main.dataset.czPdpChaptersMounted) return null;
      const chapters = definitions.map(({kind, title}) => engine.chapter(kind, title, product));
      main.dataset.czChapter = 'customize';
      main.dataset.czPdpChaptersMounted = product;
      main.after(...chapters);
      return Object.fromEntries(chapters.map((node) => [node.dataset.czChapter, node]));
    },
    step(index, title, product = config.kind, key = '') {
      const node = document.createElement('section');
      node.className = `cz-pdp-step cz-${product}-step`;
      node.dataset.czStep = String(index);
      if (key) node.dataset.czStepKey = key;
      node.dataset.czComponentSource = DRAPERY_COMPONENT_VERSION;
      if (engine.master === 'drapery') node.classList.add('cz-config-step');
      const heading = document.createElement('h3');
      heading.className = 'cz-pdp-step-heading';
      if (engine.master === 'drapery') heading.classList.add('cz-config-heading');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cz-pdp-step-toggle';
      if (engine.master === 'drapery') button.classList.add('cz-config-toggle');
      if (product === 'rylan') { button.classList.add('cz-rylan-option-step'); button.dataset.czRylanGroup = String(index); }
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', engine.master === 'drapery' ? `cz-config-panel-${index}` : `cz-${product}-step-panel-${index}`);
      const number = document.createElement('span');
      number.className = 'cz-pdp-step-number';
      number.classList.add(engine.master === 'drapery' ? 'cz-config-number' : 'cz-rylan-option-step__number');
      number.textContent = String(index + 1);
      const label = document.createElement('span');
      label.className = 'cz-pdp-step-label';
      label.classList.add(engine.master === 'drapery' ? 'cz-config-label' : 'cz-rylan-option-step__label');
      const name = document.createElement('span');
      name.className = 'cz-pdp-step-title';
      name.classList.add(engine.master === 'drapery' ? 'cz-config-title' : 'cz-rylan-option-step__title');
      name.textContent = title;
      const summary = document.createElement('span');
      summary.className = 'cz-pdp-step-summary';
      summary.classList.add(engine.master === 'drapery' ? 'cz-config-summary' : 'cz-rylan-option-step__summary');
      label.append(name, summary);
      const symbol = document.createElement('span');
      symbol.className = 'cz-pdp-step-symbol';
      symbol.classList.add(engine.master === 'drapery' ? 'cz-config-symbol' : 'cz-rylan-option-step__symbol');
      symbol.setAttribute('aria-hidden', 'true');
      button.append(number, label, symbol);
      heading.append(button);
      const panel = document.createElement('div');
      panel.className = 'cz-pdp-step-panel';
      if (engine.master === 'drapery') panel.classList.add('cz-config-panel');
      panel.id = engine.master === 'drapery' ? `cz-config-panel-${index}` : `cz-${product}-step-panel-${index}`;
      panel.hidden = true;
      const error = document.createElement('p');
      error.className = 'cz-pdp-step-error';
      if (engine.master === 'drapery') error.classList.add('cz-config-error');
      error.setAttribute('role', 'alert');
      error.hidden = true;
      panel.append(error);
      node.append(heading, panel);
      return node;
    },
    stepHelp(heading, definition, index, options = {}) {
      if (!heading || !definition?.help) return null;
      let button = heading.querySelector(':scope > .cz-step-help');
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'cz-pdp-step-help cz-step-help';
        button.dataset.czComponentSource = DRAPERY_COMPONENT_VERSION;
        button.innerHTML = '<span class="cz-help-icon" aria-hidden="true">?</span>';
        heading.append(button);
      }
      if (options.className) button.classList.add(options.className);
      const help = definition.help;
      button.dataset.czHelpKind = options.kind || help.kind || `step-${index + 1}`;
      button.setAttribute('aria-label', options.label || help.label || `About ${definition.title}`);
      button.setAttribute('aria-haspopup', 'dialog');
      if (options.ariaControls) button.setAttribute('aria-controls', options.ariaControls);
      const position = () => {
        const title = heading.querySelector('.cz-config-title');
        if (!title?.getBoundingClientRect().width) return;
        const headingRect = heading.getBoundingClientRect();
        const titleRect = title.getBoundingClientRect();
        button.style.left = `${Math.max(0, Math.min(titleRect.right - headingRect.left + 6, headingRect.width - 40))}px`;
        button.style.top = `${titleRect.top - headingRect.top + titleRect.height / 2}px`;
      };
      requestAnimationFrame(position);
      if (!heading.dataset.czSharedHelpObserved && window.ResizeObserver) {
        heading.dataset.czSharedHelpObserved = 'true';
        new ResizeObserver(position).observe(heading);
      }
      return button;
    },
    continueButton(label, className = '') {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = ['cz-pdp-step-continue', className].filter(Boolean).join(' ');
      button.dataset.czComponentSource = DRAPERY_COMPONENT_VERSION;
      button.textContent = label;
      return button;
    },
    positionBelowSticky(target) {
      if (!target?.isConnected || !target.getBoundingClientRect().height) return;
      const headerBottom = Math.max(0, document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
      const sticky = productForm.querySelector('.app-title-price-wrapper');
      const stickyStyle = sticky && getComputedStyle(sticky);
      const stickyBottom = stickyStyle?.position === 'sticky'
        ? Math.max(0, sticky.getBoundingClientRect().bottom)
        : 0;
      const clearance = Math.max(24, headerBottom, stickyBottom) + 16;
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - clearance);
      if (Math.abs(top - window.scrollY) <= 2) return;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      window.scrollTo({top, behavior});
    },
    activate(root, index, selectors = {}) {
      const stepSelector = selectors.step || '.cz-pdp-step';
      const toggleSelector = selectors.toggle || '.cz-pdp-step-toggle';
      const panelSelector = selectors.panel || '.cz-pdp-step-panel';
      root.querySelectorAll(stepSelector).forEach((step) => {
        const active = Number(step.dataset.czStep) === index;
        const toggle = step.querySelector(toggleSelector);
        const panel = step.querySelector(panelSelector);
        if (toggle && toggle.getAttribute('aria-expanded') !== String(active)) toggle.setAttribute('aria-expanded', String(active));
        if (panel && panel.hidden === active) panel.hidden = !active;
      });
    },
    controller(root, options) {
      let active = options.initial ?? 0;
      const apply = (index, focus = false) => {
        active = index;
        if (options.apply) options.apply(index);
        else engine.activate(root, index);
        if (focus && index >= 0) options.position?.(index);
      };
      return {
        get active() { return active; },
        open: apply,
        toggle(index, focus = true) { apply(active === index ? -1 : index, focus); },
        next(index) { if (index < options.count - 1) apply(index + 1, true); else options.onComplete?.(); },
        bind(button, index, focusOnClick = () => true) { button.addEventListener('click', (event) => {
          options.beforeToggle?.();
          if (options.onToggle) options.onToggle(index);
          else this.toggle(index, focusOnClick(event));
        }); }
      };
    },
    guided(root, options) {
      const definitions = options.definitions || config.groups || [];
      const product = options.product || config.profile || config.kind;
      root.classList.add('cz-pdp-guided-root');
      root.dataset.czComponentSource = DRAPERY_COMPONENT_VERSION;
      const controller = engine.controller(root, {
        count: definitions.length,
        initial: options.initial ?? 0,
        onToggle: options.onToggle,
        apply: options.apply || ((index) => engine.activate(root, index)),
        position: options.position
      });
      const ensureStep = (index, title = definitions[index]?.title) => {
        let step = root.querySelector(`.cz-pdp-step[data-cz-step="${index}"]`);
        if (step) {
          if (definitions[index]?.key) step.dataset.czStepKey = definitions[index].key;
        } else {
          step = engine.step(index, title, product, definitions[index]?.key || '');
          const outside = [...root.children].find((child) => !child.classList.contains('cz-pdp-step'));
          root.insertBefore(step, outside || null);
          controller.bind(step.querySelector('.cz-pdp-step-toggle'), index);
        }
        const collectionId = config.collection?.id || '';
        const collection = collectionId
          ? root.querySelector(`.ymq-options-box[ymq-option-id="${collectionId}"]`)
          : root.querySelector('.ymq-options-box[ymq-option-label="Collection"]');
        if (collection) {
          syncCollectionDisplayNames(collection);
          collection.classList.add('cz-collection-before-color');
          collection.dataset.czCollectionBeforeColor = '';
          const orderedSteps = [...root.querySelectorAll(':scope > .cz-pdp-step')]
            .sort((left, right) => Number(left.dataset.czStep) - Number(right.dataset.czStep));
          orderedSteps.forEach(node => root.append(node));
          root.prepend(collection);
        }
        return step;
      };
      return {controller, ensureStep};
    },
    bindStepHelp(root, definitions) {
      if (!root || root.dataset.czStepHelpBound) return;
      root.dataset.czStepHelpBound = 'true';
      root.addEventListener('click', (event) => {
        const button = event.target.closest?.('.cz-step-help[data-cz-help-kind]');
        if (!button || !root.contains(button) || button.classList.contains('cz-tarryn-help')) return;
        const step = button.closest('.cz-pdp-step');
        const definition = definitions[Number(step?.dataset.czStep)];
        const help = definition?.help;
        if (!help) return;
        let dialog = document.getElementById('cz-pdp-step-help-dialog');
        if (!dialog) {
          dialog = document.createElement('dialog');
          dialog.id = 'cz-pdp-step-help-dialog';
          dialog.className = 'cz-pdp-step-help-dialog';
          dialog.innerHTML = '<div class="cz-pdp-step-help-dialog__body"><button type="button" class="cz-pdp-step-help-dialog__close" aria-label="Close">×</button><h2></h2><p></p><a hidden></a></div>';
          document.body.append(dialog);
          dialog.querySelector('button').addEventListener('click', () => dialog.close());
          dialog.addEventListener('click', (click) => { if (click.target === dialog) dialog.close(); });
        }
        const title = dialog.querySelector('h2');
        const copy = dialog.querySelector('p');
        const link = dialog.querySelector('a');
        title.textContent = help.title || definition.title;
        copy.textContent = help.text || '';
        link.hidden = !help.href;
        if (help.href) { link.href = help.href; link.textContent = help.linkText || 'View guide'; }
        dialog.addEventListener('close', () => button.focus({preventScroll: true}), {once: true});
        dialog.showModal();
      });
    },
    activeBox(box, exclude = '') {
      return !box.hidden && getComputedStyle(box).display !== 'none' && box.dataset.czHandwovenVisualSuppressed !== 'true' && !box.classList.contains('ymq-option-condition-hide') &&
        [...box.querySelectorAll('input, select, textarea')].some((input) => !input.disabled && input.type !== 'hidden' && (!exclude || !input.matches(exclude)));
    },
    values(box, exclude = '') {
      return [...box.querySelectorAll('input:checked:not(:disabled), select:not(:disabled), textarea:not(:disabled), input.ymq-input-text:not(:disabled)')]
        .filter((input) => !exclude || !input.matches(exclude))
        .map((input) => input.value.trim()).filter(Boolean);
    },
    missingRequired(boxes, exclude = '') {
      return boxes.find((box) => engine.activeBox(box, exclude) && /\brequired\b/.test(box.getAttribute('ymq-option-validate') || '') && !engine.values(box, exclude).length);
    },
    details(contents, titles) {
      const info = document.createElement('div');
      info.className = 'cz-pdp-info';
      info.setAttribute('aria-label', 'Product information');
      contents.forEach((content, index) => {
        const item = document.createElement('details');
        const summary = document.createElement('summary');
        summary.textContent = titles[index] || `More information ${index + 1}`;
        item.append(summary, content);
        item.addEventListener('toggle', () => {
          if (item.open) info.querySelectorAll('details').forEach((other) => { if (other !== item) other.open = false; });
        });
        info.append(item);
      });
      return info;
    },
    supportContact(root) {
      if (!root) return false;
      const popupOnly = config.master === 'drapery'
        && ['11719366345022', '12044121178430', '12032865698110'].includes(String(config.productId));
      const form = root.querySelector('form.g-container');
      if (!form) return false;
      let card = root.querySelector('.cz-consult-card');
      if (card?.czContactForm === form && form.hasAttribute('data-cz-contact-fields')) return true;
      let button = card?.querySelector('button');
      if (!card) {
        card = document.createElement('div');
        card.className = 'cz-consult-card';
        card.dataset.czComponentSource = DRAPERY_COMPONENT_VERSION;
        const heading = document.createElement('h2');
        heading.textContent = 'We’re here to help';
        const intro = document.createElement('p');
        intro.textContent = 'Questions about measurements, fabric or your order? Our team can help.';
        button = document.createElement('button');
        button.type = 'button';
        button.textContent = 'Send a message';
        button.setAttribute('aria-expanded', 'false');
        const zoom = document.createElement('a');
        zoom.href = '/products/complimentary-1-on-1-zoom-consultation';
        zoom.textContent = 'Book a free Zoom consultation →';
        zoom.className = 'cz-tarryn-help';
        zoom.dataset.czHelpKind = 'consultation';
        zoom.setAttribute('aria-haspopup', 'dialog');
        card.append(heading, intro, button, zoom);
        form.before(card);
        if (popupOnly) {
          button.setAttribute('aria-haspopup', 'dialog');
          window.addEventListener('cz-contact-state', event => {
            button.setAttribute('aria-expanded', String(event.detail?.open === true));
          });
        }
        button.addEventListener('click', () => {
          const currentForm = root.querySelector('form.g-container');
          if (popupOnly) {
            if (currentForm) currentForm.hidden = true;
            window.dispatchEvent(new CustomEvent('cz-contact-open', {detail: {source: 'launcher'}}));
            return;
          }
          if (!currentForm) return;
          currentForm.hidden = !currentForm.hidden;
          button.setAttribute('aria-expanded', String(!currentForm.hidden));
          button.textContent = currentForm.hidden ? 'Send a message' : 'Hide message form';
          if (!currentForm.hidden) currentForm.querySelector('.cz-name-field input, input:not([type="hidden"]), textarea')?.focus({preventScroll: true});
        });
      }
      const formHost = form.closest('[id]');
      if (popupOnly) button.setAttribute('aria-controls', 'cz-contact-dialog');
      else if (formHost?.id) button.setAttribute('aria-controls', formHost.id);
      card.czContactForm = form;
      form.dataset.czContactFields = '';
      form.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      button.textContent = 'Send a message';
      return true;
    },
    openDrawer(drawer, opener, closeButton, state) {
      if (opener) state.opener = opener;
      if (drawer.getAttribute('aria-hidden') === 'true') state.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('ymq-modal-open');
      closeButton?.focus({preventScroll: true});
    },
    closeDrawer(drawer, state) {
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('ymq-modal-open');
      document.body.style.overflow = state.previousOverflow || '';
      state.opener?.focus({preventScroll: true});
    },
    bindDialog(dialog, closeButton, openers) {
      let opener;
      openers.forEach(({button, beforeOpen}) => button?.addEventListener('click', (event) => {
        beforeOpen?.(event);
        if (event.defaultPrevented && !beforeOpen) return;
        opener = button;
        if (!dialog.open) dialog.showModal();
        closeButton?.focus({preventScroll: true});
      }));
      closeButton?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => opener?.focus({preventScroll: true}));
    }
  };
  window.czPdpShared = engine;

  if (config.features?.guidedOptions === true && config.features?.guidedAdapter === 'legacy-shared') {
    const mountGuidedProfile = () => {
      const root = document.querySelector(`ymq-option[ymq-option-product-id="${config.productId}"]`);
      const color = root?.querySelector('[ymq-option-label^="Color"]');
      const configuredIds = [
        ...(config.collection?.id ? [config.collection.id] : []),
        ...(config.presentationHiddenIds || []),
        ...config.groups.flatMap((group) => group.optional ? [] : (group.ids || []))
      ];
      if (!root || !color || configuredIds.some((id) => !root.querySelector(`[ymq-option-id="${id}"]`))) return false;
      if (root.dataset.czPdpGuidedReady) return true;
      (config.presentationHiddenIds || []).forEach((id) => {
        const owner = root.querySelector(`[ymq-option-id="${id}"]`);
        if (!owner) return;
        owner.hidden = true;
        owner.dataset.czPresentationHidden = '';
        owner.setAttribute('aria-hidden', 'true');
      });
      const positionHandwovenStep = (index) => {
        if (config.controller !== 'handwoven-shade') return;
        const step = root.querySelector(`.cz-pdp-step[data-cz-step="${index}"]`);
        const heading = step?.querySelector('.cz-pdp-step-heading');
        if (!heading) return;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!heading.isConnected || step.querySelector('.cz-pdp-step-toggle')?.getAttribute('aria-expanded') !== 'true') return;
          engine.positionBelowSticky(heading);
        }));
      };
      const guided = engine.guided(root, {definitions: config.groups, product: config.profile, initial: 0});
      const steps = config.groups.map((group, index) => {
        const step = guided.ensureStep(index, group.title);
        const help = engine.stepHelp(step.querySelector('.cz-pdp-step-heading'), group, index);
        if (engine.master === 'drapery' && help) {
          help.classList.add('cz-tarryn-help');
          help.setAttribute('aria-controls', 'cz-tarryn-help-drawer');
        }
        const panel = step.querySelector('.cz-pdp-step-panel');
        const groupNodes = (group.ids || []).map((id) => root.querySelector(`[ymq-option-id="${id}"]`)).filter(Boolean);
        const preambleIds = new Set(group.preambleIds || []);
        const preambleNodes = groupNodes.filter((node) => preambleIds.has(node.getAttribute('ymq-option-id')));
        const remainingNodes = groupNodes.filter((node) => !preambleIds.has(node.getAttribute('ymq-option-id')) && node !== color);
        const nodes = group.key === 'color' ? [...preambleNodes, color, ...remainingNodes] : groupNodes;
        nodes.forEach((node) => { if (node.parentElement !== panel) panel.append(node); });
        if (Array.isArray(group.segments)) group.segments.forEach((segment) => {
          let region = panel.querySelector(`:scope > .cz-pdp-step-segment[data-cz-segment="${segment.key}"]`);
          if (!region) {
            region = document.createElement('section');
            region.className = 'cz-pdp-step-segment';
            region.dataset.czSegment = segment.key;
            const segmentHeading = document.createElement('h4');
            segmentHeading.className = 'cz-pdp-step-segment__heading';
            segmentHeading.textContent = segment.title;
            region.append(segmentHeading);
            panel.append(region);
          }
          (segment.ids || []).forEach((id) => {
            const node = root.querySelector(`[ymq-option-id="${id}"]`);
            if (node && node.parentElement !== region) region.append(node);
          });
          region.querySelectorAll('.ymq-option-title').forEach((title) => {
            const label = title.querySelector('.ymq_lable');
            title.classList.toggle('cz-pdp-segment-repeated-title', label?.textContent.trim() === segment.title.trim());
          });
        });
        const installationIds = new Set(group.segments?.find(segment => segment.key === 'installation')?.ids || []);
        const captionNodes = group.key === 'color' ? nodes : nodes.filter(node => group.key === 'installation' || installationIds.has(node.getAttribute('ymq-option-id')));
        captionNodes.forEach((node) => {
          node.querySelectorAll('.ymq-option-label').forEach((label) => {
            const input = label.querySelector('input[type="radio"]');
            if (!input || !label.querySelector('img') || label.querySelector('.cz-color-caption')) return;
            const caption = document.createElement('span');
            caption.className = 'cz-color-caption';
            const name = document.createElement('span');
            name.className = 'cz-color-name';
            name.textContent = group.key === 'color' ? input.value.split('-')[0].trim() : input.value.trim();
            caption.append(name);
            label.append(caption);
          });
        });
        if (group.key === 'header') nodes.forEach((node) => {
          node.querySelector('.ymq-option-value-info')?.classList.add('cz-header-grid');
          node.querySelectorAll('.ymq-option-label').forEach((label) => {
            const input = label.querySelector('input[type="radio"]');
            if (!input || !label.querySelector('img') || label.querySelector('.cz-header-name')) return;
            const name = document.createElement('span');
            name.className = 'cz-header-name';
            name.textContent = input.value.split('|')[0].trim();
            label.append(name);
          });
        });
        if (index < config.groups.length - 1) {
          let next = panel.querySelector(':scope > .cz-pdp-step-continue');
          if (!next) {
            next = engine.continueButton(`Continue to ${config.groups[index + 1].title}`);
            next.addEventListener('click', () => {
              if (!validateStep(index, true)) return;
              guided.controller.next(index);
              positionHandwovenStep(index + 1);
            });
            panel.append(next);
          }
        }
        return step;
      });
      const boxesFor = (index) => [...steps[index].querySelectorAll('[ymq-option-id]')];
      const focusTarget = (box) => {
        const fields = [...(box?.querySelectorAll('select:not(:disabled), input:not([type="hidden"]):not(:disabled), textarea:not(:disabled)') || [])];
        const visible = fields.find((field) => field.classList.contains('cz-handwoven-size-input') && field.getClientRects().length)
          || fields.find((field) => field.getClientRects().length && getComputedStyle(field).visibility !== 'hidden' && !field.classList.contains('cz-handwoven-size-native'));
        if (visible) return visible;
        if (box && !box.hasAttribute('tabindex')) box.tabIndex = -1;
        return box;
      };
      const validateStep = (index, showError = false) => {
        const missing = engine.missingRequired(boxesFor(index));
        const error = steps[index].querySelector('.cz-pdp-step-error');
        if (!missing) {
          if (error) error.hidden = true;
          return true;
        }
        if (showError && error) {
          error.textContent = `Please complete ${config.groups[index].title.toLowerCase()} to continue.`;
          error.hidden = false;
          guided.controller.open(index, false);
          focusTarget(missing)?.focus({preventScroll: true});
          requestAnimationFrame(() => error.scrollIntoView({behavior: 'instant', block: 'center'}));
        }
        return false;
      };
      const updateSummaries = () => {
        steps.forEach((step, index) => {
          const values = boxesFor(index).flatMap((box) => engine.activeBox(box) ? engine.values(box).filter((value) => value !== '0') : []);
          const summary = step.querySelector('.cz-pdp-step-summary');
          const nextSummary = [...new Set(values)].slice(0, 3).join(', ') || config.groups[index].emptySummary || '';
          if (summary && summary.textContent !== nextSummary) summary.textContent = nextSummary;
        });
      };
      root.addEventListener('change', updateSummaries);
      root.addEventListener('input', updateSummaries);
      const conditionObserver = new MutationObserver(updateSummaries);
      conditionObserver.observe(root, {attributes: true, attributeFilter: ['class', 'hidden'], subtree: true});
      const add = productForm.querySelector('button[name="add"], input[name="add"], .product-form__submit');
      add?.addEventListener('click', (event) => {
        const invalid = config.groups.findIndex((_, index) => !validateStep(index));
        if (invalid < 0) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        validateStep(invalid, true);
      }, true);
      engine.activate(root, 0);
      engine.bindStepHelp(root, config.groups);
      updateSummaries();
      root.dataset.czPdpGuidedReady = config.profile;
      return true;
    };
    if (!mountGuidedProfile()) {
      const observer = new MutationObserver(() => { if (mountGuidedProfile()) observer.disconnect(); });
      observer.observe(productForm, {childList: true, subtree: true});
      setTimeout(() => observer.disconnect(), 30000);
    }
  }

  if (config.swatchGuidance) {
    const applySwatchGuidance = () => {
      const box = document.querySelector(`.ymq-options-box[ymq-option-id="${config.swatchGuidance.optionId}"] .ymq-option-custome-text`);
      if (!box || box.dataset.czPdpGuidanceApplied) return false;
      const paragraph = document.createElement('p');
      const label = document.createElement('strong');
      label.textContent = config.swatchGuidance.label;
      const link = document.createElement('a');
      link.href = config.swatchGuidance.href;
      link.textContent = config.swatchGuidance.linkText;
      paragraph.append(label, document.createTextNode(' — '), link, document.createTextNode(`. ${config.swatchGuidance.text}`));
      box.replaceChildren(paragraph);
      box.dataset.czPdpGuidanceApplied = 'true';
      return true;
    };
    if (!applySwatchGuidance()) {
      const observer = new MutationObserver(() => { if (applySwatchGuidance()) observer.disconnect(); });
      observer.observe(productForm, {childList: true, subtree: true});
      setTimeout(() => observer.disconnect(), 30000);
    }
  }

  if (config.visibleFacts) {
    const mountFacts = () => {
      const material = [...productForm.querySelectorAll('strong')].find((node) => node.textContent.trim() === 'Material:');
      const details = material?.closest('.metafield-rich_text_field');
      if (!details || details.querySelector('[data-cz-pdp-profile-facts]')) return Boolean(details);
      const facts = document.createElement('div');
      facts.className = 'cz-pdp-profile-facts';
      facts.dataset.czPdpProfileFacts = config.profile;
      config.visibleFacts.forEach(({label, value}) => {
        const row = document.createElement('p');
        const term = document.createElement('strong');
        term.textContent = `${label}:`;
        row.append(term, document.createTextNode(` ${value}`));
        facts.append(row);
      });
      material.parentElement.after(facts);
      return true;
    };
    if (!mountFacts()) document.addEventListener('DOMContentLoaded', mountFacts, {once: true});
  }
})();
